class QuickJumpBar extends HTMLElement {
    constructor() {
        super();
        this.items = [];
        this.sticky = false;
        this.announcementBarHeight = 0;
        this.scrollSpyTicking = false;
        this.programmaticScroll = false;
        this.programmaticTargetId = null;
        this.programmaticUnlockTimer = null;
    }

    connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.quick-jump-bar-item'));
        this.sticky = this.dataset.sticky === 'true';

        this.enableSticky();
        this.hasAnnouncementBar();
        this.attachEvents();
        this.showJumpBar();
        this.inTheArea();
    }

    enableSticky() {
        if (this.sticky) {
            document.body.classList.add('header_no_sticky');
        }
    }

    hasAnnouncementBar() {
        const announcementBar = document.querySelector('.announcement-bar-section');
        if (!announcementBar) return;

        const updateHeight = () => {
            this.announcementBarHeight = announcementBar.getBoundingClientRect().height;
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
    }

    getQuickJumpBarSection() {
        return this.closest('.section-quick-jump-bar') || document.querySelector('.section-quick-jump-bar');
    }

    getQuickJumpBarHeight() {
        const barSection = this.getQuickJumpBarSection();
        if (barSection) {
            return barSection.getBoundingClientRect().height;
        }
        return this.getBoundingClientRect().height;
    }

    isQuickJumpBarFixed() {
        const barSection = this.getQuickJumpBarSection();
        return Boolean(barSection?.classList.contains('active'));
    }

    getScrollOffset() {
        let offset = this.announcementBarHeight || 0;

        if (this.sticky && this.isQuickJumpBarFixed()) {
            offset += this.getQuickJumpBarHeight();
        }

        return offset + 12;
    }

  /**
   * 页面上若存在重复 id，取 MainContent 内文档位置最靠下的那一个（秋促正文区块）。
   */
    resolveTargetElement(targetId) {
        if (!targetId) return null;

        const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(targetId) : targetId;
        const nodes = document.querySelectorAll(`#${escaped}`);
        if (!nodes.length) return null;
        if (nodes.length === 1) return nodes[0];

        const main = document.getElementById('MainContent');
        let best = nodes[0];
        let bestTop = -Infinity;

        nodes.forEach((node) => {
            if (main && !main.contains(node)) return;
            const top = node.getBoundingClientRect().top + window.scrollY;
            if (top > bestTop) {
                bestTop = top;
                best = node;
            }
        });

        return best;
    }

    getScrollElement(targetId) {
        const target = this.resolveTargetElement(targetId);
        if (!target) return null;

        if (
            target.matches(
                'section.feature-scenes-v3, section.product-bundle-hero-v2, section.product-sales-v4, section.lifestyle-gallery-v2'
            )
        ) {
            return target;
        }

        if (target.classList.contains('shopify-section')) {
            return target;
        }

        return target.closest('.shopify-section') || target;
    }

    getElementDocumentTop(element) {
        return element.getBoundingClientRect().top + window.scrollY;
    }

    getTargetRegion(targetId) {
        return this.getScrollElement(targetId);
    }

    setActive(targetId) {
        if (!targetId) return;

        this.items.forEach((item) => {
            const id = item.getAttribute('data-target-id');
            item.classList.toggle('active', id === targetId);
        });
    }

    inTheArea() {
        if (!this.items.length) return;

        if (this.programmaticScroll && this.programmaticTargetId) {
            this.setActive(this.programmaticTargetId);
            return;
        }

        const probe = window.scrollY + this.getScrollOffset() + 2;
        let activeId = null;

        this.items.forEach((item) => {
            const targetId = item.getAttribute('data-target-id');
            if (!targetId) return;

            const region = this.getTargetRegion(targetId);
            if (!region) return;

            if (this.getElementDocumentTop(region) <= probe) {
                activeId = targetId;
            }
        });

        if (!activeId && this.items[0]) {
            activeId = this.items[0].getAttribute('data-target-id');
        }

        this.setActive(activeId);
    }

    onScrollSpy() {
        if (this.scrollSpyTicking) return;
        this.scrollSpyTicking = true;
        requestAnimationFrame(() => {
            this.inTheArea();
            this.scrollSpyTicking = false;
        });
    }

    beginProgrammaticScroll(targetId) {
        this.programmaticScroll = true;
        this.programmaticTargetId = targetId;
        this.setActive(targetId);

        if (this.programmaticUnlockTimer) {
            clearTimeout(this.programmaticUnlockTimer);
        }

        const unlock = () => {
            window.removeEventListener('scrollend', unlock);
            this.programmaticUnlockTimer = setTimeout(() => {
                this.programmaticScroll = false;
                this.programmaticTargetId = null;
                this.inTheArea();
            }, 80);
        };

        // scrollend 在部分浏览器可用；否则用超时兜底
        window.addEventListener('scrollend', unlock, { once: true });
        this.programmaticUnlockTimer = setTimeout(unlock, 900);
    }

    jumpTo(event) {
        event.preventDefault();

        const targetId = event.currentTarget.getAttribute('data-target-id');
        const scrollElement = this.getScrollElement(targetId);
        if (!scrollElement) return;

        const scrollTop = Math.max(0, this.getElementDocumentTop(scrollElement) - this.getScrollOffset() + 2);

        this.beginProgrammaticScroll(targetId);

        window.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
        });

        this.scrollItemToCenter(event.currentTarget);
    }

    scrollItemToCenter(item) {
        const wrapper = this.querySelector('.quick-item-box');
        if (!wrapper) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const wrapperScrollLeft = wrapper.scrollLeft;

        const offsetLeft =
            itemRect.left -
            wrapperRect.left +
            wrapperScrollLeft -
            wrapperRect.width / 2 +
            itemRect.width / 2;

        wrapper.scrollTo({
            left: offsetLeft,
            behavior: 'smooth',
        });
    }

    scrollToItem(item) {
        const wrapper = item.parentElement;
        if (!wrapper) return;

        const itemRect = item.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();

        const offsetLeft = itemRect.left - wrapperRect.left + wrapper.scrollLeft;
        wrapper.scrollTo({
            left: offsetLeft,
            behavior: 'smooth',
        });
    }

    showJumpBar() {
        const quickJumpBarSection = this.getQuickJumpBarSection();
        const placeholderDiv = document.createElement('div');
        let placeholderAdded = false;
        let isQuickJumpBarActive = false;

        const rect = this.getBoundingClientRect();
        const mainProductContainerBottom = rect.bottom + window.scrollY;

        if (!quickJumpBarSection) return;

        window.addEventListener('scroll', () => {
            if (window.scrollY > mainProductContainerBottom) {
                if (!isQuickJumpBarActive) {
                    if (!placeholderAdded) {
                        placeholderDiv.classList.add('quick-jump-bar-placeholder');
                        placeholderDiv.style.height = `${quickJumpBarSection.offsetHeight}px`;
                        placeholderDiv.style.width = '100%';
                        placeholderDiv.style.display = 'block';
                        quickJumpBarSection.before(placeholderDiv);
                        placeholderAdded = true;
                    }

                    quickJumpBarSection.classList.add('loading');
                    quickJumpBarSection.offsetHeight;
                    quickJumpBarSection.classList.add('active');

                    setTimeout(() => {
                        quickJumpBarSection.classList.remove('loading');
                    }, 50);
                    isQuickJumpBarActive = true;
                }
            } else if (isQuickJumpBarActive) {
                quickJumpBarSection.classList.remove('active');
                isQuickJumpBarActive = false;

                if (placeholderAdded) {
                    placeholderDiv.remove();
                    placeholderAdded = false;
                }
            }
        });
    }

    attachEvents() {
        this.items.forEach((item) => {
            item.addEventListener('click', (event) => {
                this.jumpTo(event);
                this.scrollToItem(item);
            });
        });

        window.addEventListener('scroll', () => this.onScrollSpy(), { passive: true });
    }
}

customElements.define('quick-jump-bar', QuickJumpBar);
